import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { fileSizeZeroCopy } from 'design-system/src/sharedCopy';
import { Typography } from 'design-system/src/components/Typography/Typography';
import {
  client,
  NewCommentFile,
  FileType,
  InsuranceOption,
  OutboundTaskType,
  OutboundTaskTypeKey,
} from '../../../../api';
import type { DocumentUploadTask, WithAdditionalTaskResponseFields } from '../../types';
import { useContext, useState } from 'react';
import { TaskContext } from '../../contexts';
import Button from 'design-system/src/components/Button/Button';
import { useNavigate } from 'react-router-dom';
import DeclineModal from '../../DeclineModal';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { InstructionsContainer } from '../../TaskView';
import { TaskInstructionSummary, FileUploadField, InsuranceCardSection } from '../../shared';
import { AttachedFile } from '../../Files';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton.tsx';
import { StructuredTaskViewWrapper } from '..';
import { createTrackingProps } from 'shared-utils/analytics';
import { validateFiles } from '../../../../utils';

// Keep this in sync with FILE_SIZE_ZERO_ERROR_MESSAGE in partner_portal/task_helpers.py
const FILE_SIZE_ZERO_ERROR_MESSAGE = 'file_size_zero';
// Keep this in sync with MEDICAL_RECORDS_REQUIRED_ERROR_MESSAGE in partner_portal/task_helpers.py
const MEDICAL_RECORDS_REQUIRED_ERROR_MESSAGE = 'medical_records_required';

const MedicalRecordsSection: React.FC<{
  files: NewCommentFile[];
  handleFileDelete: (filename: string, document_type: FileType) => void;
  handleFileUpload: (newFiles: NewCommentFile[], section: 'insurance' | 'medical') => void;
  isTaskCompleted: boolean;
  uploadedFileIds: string[];
  fileValidationError: string | null;
  showMissingMedicalRecordsError: boolean;
}> = ({
  files,
  handleFileDelete,
  handleFileUpload,
  isTaskCompleted,
  uploadedFileIds,
  fileValidationError,
  showMissingMedicalRecordsError,
}) => {
  if (isTaskCompleted) {
    return uploadedFileIds.length > 0 ? (
      <div className="flex flex-wrap gap-2">
        {uploadedFileIds.map((fileId) => (
          <AttachedFile key={fileId} fileId={fileId} />
        ))}
      </div>
    ) : (
      <Typography styledAs="bodySmallDMSans">No medical records uploaded</Typography>
    );
  }

  return (
    <FileUploadField
      label="Additional documents"
      labelId="medical-records-label"
      instructions="Please upload any relevant visit notes or supporting clinical documentation."
      uploadedFiles={files}
      onFileDelete={handleFileDelete}
      onFilesUpload={(newFiles) => handleFileUpload(newFiles, 'medical')}
      fileTypesOptions={['Medical Records']}
      errors={[
        {
          message: 'Please upload medical records to submit this task',
          visible: !!showMissingMedicalRecordsError,
        },
        {
          message: fileValidationError || '',
          visible: !!fileValidationError,
        },
      ]}
    />
  );
};

const DocumentUploadTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<DocumentUploadTask>;
}> = ({ taskResponse }) => {
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);
  const [showHelpRequestModal, setShowHelpRequestModal] = useState(false);
  const [files, setFiles] = useState<NewCommentFile[]>([]);
  const [insuranceOption, setInsuranceOption] = useState<InsuranceOption | undefined>();
  const [showMissingInsuranceError, setShowMissingInsuranceError] = useState(false);
  const [showMissingMedicalRecordsError, setShowMissingMedicalRecordsError] = useState(false);
  const [insuranceFileValidationError, setInsuranceFileValidationError] = useState<string | null>(null);
  const [medicalRecordsFileValidationError, setMedicalRecordsFileValidationError] = useState<string | null>(null);

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();

  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const submit = useMutation<void, Error, InsuranceOption>({
    mutationFn: async (insuranceOptionValue: InsuranceOption) => {
      const formData = new FormData();

      files.forEach((fileInfo) => {
        if (fileInfo.document_type === 'Insurance Card Both Sides') {
          formData.append('insurance_card_files[]', fileInfo.file);
        } else if (fileInfo.document_type === 'Medical Records') {
          formData.append('medical_record_files[]', fileInfo.file);
        }
      });

      formData.append('insurance_option', insuranceOptionValue);

      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking submit documents requested for ID: ${taskId}`);
        return;
      }

      return await client.post(`tasks/${taskId}/submit-documents-requested`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      navigate('/tasks');
      toast.custom(() => <Toast variant="success">Document upload task submitted successfully</Toast>);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error.message;
      if (message === FILE_SIZE_ZERO_ERROR_MESSAGE) {
        toast.custom(() => <Toast variant="error">{fileSizeZeroCopy}</Toast>);
      } else if (message === MEDICAL_RECORDS_REQUIRED_ERROR_MESSAGE) {
        toast.custom(() => <Toast variant="error">Medical records are required to submit this task</Toast>);
        setShowMissingMedicalRecordsError(true);
      } else if (message) {
        toast.custom(() => <Toast variant="error">{message}</Toast>);
      } else {
        toast.custom(() => <StandardErrorToast />);
      }
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const handleFileUpload = (newFiles: NewCommentFile[], section: 'insurance' | 'medical') => {
    setInsuranceFileValidationError(null);
    setMedicalRecordsFileValidationError(null);

    // Validate files first
    const validationError = validateFiles(newFiles);
    if (validationError) {
      if (section === 'insurance') {
        setInsuranceFileValidationError(validationError);
      } else {
        setMedicalRecordsFileValidationError(validationError);
      }
      return;
    }

    // Check for duplicate filenames - we use filenames as keys when sending to server,
    // so duplicates would cause issues downstream
    for (const fileToUpload of newFiles) {
      const existingFile = files.find((f) => f.file.name === fileToUpload.file.name);
      if (existingFile) {
        const errorMessage = `A file with the name "${fileToUpload.file.name}" has already been added. Please rename the file and try again.`;
        if (section === 'insurance') {
          setInsuranceFileValidationError(errorMessage);
        } else {
          setMedicalRecordsFileValidationError(errorMessage);
        }
        return;
      }
    }

    setFiles([...files, ...newFiles]);
    if (newFiles.some((f) => f.document_type === 'Insurance Card Both Sides')) {
      setShowMissingInsuranceError(false);
    }
    if (newFiles.some((f) => f.document_type === 'Medical Records')) {
      setShowMissingMedicalRecordsError(false);
    }
  };

  const handleInsuranceOptionChange = (option: InsuranceOption) => {
    setInsuranceOption(option);
    setShowMissingInsuranceError(false);
  };

  const handleFileDelete = (filename: string, document_type: FileType) => {
    const updatedFiles = files.filter(
      (fileInfo) => !(fileInfo.file.name === filename && fileInfo.document_type === document_type),
    );
    setFiles(updatedFiles);
  };

  const { show_update_pharmacy_button, patient, drug, pharmacy, prescriber } = taskResponse;
  const task = taskResponse.task;
  const showInsuranceOnFile = task.show_insurance_on_file ?? false;
  const isTaskCompleted = task.status === 'completed';

  const insuranceCardFiles = files.filter((file) => file.document_type === 'Insurance Card Both Sides');
  const medicalRecordsFiles = files.filter((file) => file.document_type === 'Medical Records');

  let insuranceOptionSelected: InsuranceOption | null = null;
  let instructionsMessage: React.ReactNode | null = null;
  if (isTaskCompleted) {
    if (task.insurance_option) {
      // Set insurance option to the value selected in the task response
      insuranceOptionSelected = task.insurance_option;
    } else if (task.help_request_task_relative_url) {
      instructionsMessage = (
        <InstructionsContainer>
          <Typography>
            This task was closed due to a{' '}
            <Button variant="link" onClick={() => navigate(task.help_request_task_relative_url ?? '')}>
              help request
            </Button>{' '}
            created on the original task.
          </Typography>
        </InstructionsContainer>
      );
    }
  } else {
    // Set insurance option to the selected value or the right default value
    const defaultInsuranceOption = showInsuranceOnFile ? 'use_existing_insurance' : 'new_insurance';
    insuranceOptionSelected = (insuranceOption ?? defaultInsuranceOption) as InsuranceOption;
    const instructionText = task.instructions || 'Please upload the relevant patient documentation.';
    instructionsMessage = <TaskInstructionSummary>{instructionText}</TaskInstructionSummary>;
  }

  const isMissingInsuranceFiles =
    insuranceOptionSelected === InsuranceOption.NewInsurance && insuranceCardFiles.length === 0;
  const isSubmitDisabled =
    submit.isPending || !insuranceOptionSelected || medicalRecordsFiles.length === 0 || isMissingInsuranceFiles;

  const handleSubmit = () => {
    const insuranceFiles = files.filter((file) => file.document_type === 'Insurance Card Both Sides');
    const missingInsurance = insuranceOptionSelected === InsuranceOption.NewInsurance && insuranceFiles.length === 0;
    const missingMedicalRecords = medicalRecordsFiles.length === 0;

    let hasError = false;
    if (missingInsurance) {
      setShowMissingInsuranceError(true);
      hasError = true;
    }
    if (missingMedicalRecords) {
      setShowMissingMedicalRecordsError(true);
      hasError = true;
    }
    if (hasError) {
      return;
    }
    submit.mutate(insuranceOptionSelected as InsuranceOption);
  };

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={task.type}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
          relatedPrescriptions: task.related_prescriptions,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isTaskCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: { data: drug },
          patient,
          prescriber: prescriber ?? undefined,
          pharmacy: {
            data: pharmacy ?? undefined,
            showUpdateButton: show_update_pharmacy_button,
            onUpdate: () => onTaskUpdate(),
          },
        }}
      >
        {instructionsMessage && instructionsMessage}

        <div className="mt-6 flex flex-col gap-4 rounded-medium border border-solid border-grayscale-100 p-4">
          <div className="border-grayscale-100 pb-4 border-b-solid">
            <InsuranceCardSection
              insuranceOptionSelected={insuranceOptionSelected as InsuranceOption}
              setInsuranceOption={handleInsuranceOptionChange}
              isTaskCompleted={isTaskCompleted}
              showInsuranceOnFile={showInsuranceOnFile}
              insuranceCardFiles={insuranceCardFiles}
              handleFileDelete={handleFileDelete}
              handleFileUpload={(newFiles) => handleFileUpload(newFiles, 'insurance')}
              uploadedFileIds={task.insurance_card_document_ids ?? []}
              showMissingInsuranceError={showMissingInsuranceError}
              fileValidationError={insuranceFileValidationError}
            />
          </div>

          <div className={isTaskCompleted ? '' : 'border-grayscale-100 pb-4 border-b-solid'}>
            <MedicalRecordsSection
              files={medicalRecordsFiles}
              handleFileDelete={handleFileDelete}
              handleFileUpload={handleFileUpload}
              isTaskCompleted={isTaskCompleted}
              uploadedFileIds={task.medical_records_document_ids ?? []}
              fileValidationError={medicalRecordsFileValidationError}
              showMissingMedicalRecordsError={showMissingMedicalRecordsError}
            />
          </div>

          {!isTaskCompleted && (
            <ButtonRow align={Alignment.SpaceBetween}>
              <ButtonRow>
                <Button
                  {...createTrackingProps({
                    elementName: 'Document Upload Task Page: Cancel Prescription Button',
                  })}
                  variant="tertiary"
                  onClick={() => setShowCancelConfirmationModal(true)}
                  size="sm"
                  disabled={submit.isPending}
                >
                  Cancel prescription
                </Button>

                <OtherRequestButton setShowNewFollowupTaskModal={setShowHelpRequestModal} />
              </ButtonRow>
              <Button
                {...createTrackingProps({ elementName: 'Document Upload Task Page: Submit Button' })}
                variant="primary"
                onClick={handleSubmit}
                size="sm"
                disabled={isSubmitDisabled}
              >
                Submit
              </Button>
            </ButtonRow>
          )}
        </div>
      </StructuredTaskViewWrapper>
      {showCancelConfirmationModal && (
        <DeclineModal
          closeModal={() => setShowCancelConfirmationModal(false)}
          patient={patient}
          drug={drug}
          taskKey={task.task_key}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}
      {showHelpRequestModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowHelpRequestModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key as OutboundTaskTypeKey}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default DocumentUploadTaskView;
